import * as React from "react";
import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {chunk, difference, has} from "lodash";
import {v4 as uuidv4} from 'uuid';
import {ButtonBase as Button} from "../components/Content";
import {ArticleEntity} from "../contract/article";
import useContentApi from "../hooks/useContentApi";
import useCurrentRoute from "../hooks/useCurrentRoute";
import useMedia from "../hooks/useMedia";
import {INITIAL_ROUTE} from "../hooks/useRoutes";
import {
  setArticlesIndex,
  setArticlesLoading,
  setArticlesUnavailable,
  setLoading,
  setOffline
} from "../redux/applicationActions";
import {removeArticles, setArticles} from "../redux/articleActions";
import {RootState} from "../redux/store";
import BackToTopButton from "./BackToTopButton";

interface Props {
  style?: {
    [key: string]: number|string|boolean;
  },
  query?: {
    [key: string]: string;
  }
}

const LoadMoreArticlesButton: React.FC<Props> = ({
  style = {},
  query = {}
}): React.ReactElement => {
  const contentApi = useContentApi();
  const pathname = useCurrentRoute();
  const dispatch = useDispatch();
  const {onMobile} = useMedia();
  const [busy, setBusy] = useState<boolean>(false);
  const {
    all_articles,
    articles,
    articles_unavailable,
    loading,
    next_page,
    page_size
  } = useSelector((state: RootState) => {
    const all_articles = Object.values(state.entities.articles.byId);
    const {loading, page_size, routes} = state.application;
    const route = has(routes, pathname) ? routes[pathname] : INITIAL_ROUTE;
    const {articles, articles_count, articles_unavailable, articles_visible} = route;

    const page = (articles || []).length ? Math.ceil((articles.length / page_size)) : 0;
    const total_pages = articles_count ? Math.ceil((articles_count / page_size)) : null;

    return {
      all_articles,
      articles,
      articles_unavailable,
      articles_visible: (articles_visible || []),
      loading,
      next_page: (total_pages && (page + 1) <= total_pages) ? page + 1 : false,
      page_size,
    }
  });
  const hasMore = !(loading || []).length && next_page;

  const loadMore: () => void = () => {
    if (!contentApi || !next_page || busy) return;
    let active = true;
    setBusy(true);

    const init = async (): Promise<{first: string, result: ArticleEntity[]}> => {
      const first = uuidv4();
      dispatch(setLoading([...loading || [], first]));

      // get pages
      const result = await contentApi.getArticles({
        page: next_page,
        limit: page_size,
        chunks: 1,
        ...query
      });

      if (!active) return;
      return {
        first,
        result
      };
    };

    //
    init().then(({first, result = []}: {first: string, result: ArticleEntity[]}) => {
      // maintain current order
      const remove = difference(all_articles.map(({id}) => id), articles);
      const defer = remove.map(id => all_articles.find(article => String(article.id) === String(id)));
      const update = difference(remove, [...articles, ...result.map(({id}) => String(id))]);

      dispatch(removeArticles(remove));
      dispatch(setArticles(result));
      dispatch(setArticles(update.map(id => defer.find(article => String(article.id) === String(id)))));

      dispatch(setArticlesIndex(pathname, [...articles, ...result.map(({id}) => String(id))]));
      dispatch(setLoading(difference(loading, [first])));
      setBusy(false);
      dispatch(setOffline(false));
    }).catch((e) => {
      console.error(e);

      // put application in error state
      dispatch(setOffline(true));
    });

    return (): void => {
      active = false;

      return;
    }
  };

  return articles_unavailable ? (
    <BackToTopButton style={style} />
  ) : (
    <Button
      onClick={hasMore ? loadMore : () => {
        if (busy) return;

        const ids = chunk(articles, page_size).pop(); // remaining incomplete chunk

        dispatch(setArticlesLoading(pathname, uuidv4(), ids));
        dispatch(setArticlesUnavailable(pathname));
      }}
      className={"theme--primary-color f-6 fw-600"}
      style={{height: "56px", marginBottom: `${onMobile ? 24 : 52}px`, ...style}}
    >
      {(loading || []).length ? 'Bezig met laden...' : 'Meer tonen'}
    </Button>
  )
}

export default LoadMoreArticlesButton;
